import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { VacancyPackage } from "../../types/orders-managment/packages";

interface VacancyPackagesResponse {
  data?: VacancyPackage[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useVacancyPackages = () => {
  return useQuery({
    queryKey: ["vacancyPackages"],
    queryFn: async (): Promise<VacancyPackage[]> => {
      const response = await USERAXIOS.get<
        VacancyPackagesResponse | VacancyPackage[]
      >("/vacancy_packages");

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.data) {
        return response.data.data;
      }

      return [];
    },
    enabled: true,
  });
};

export const useVacancyPackagesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["vacancyPackages", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<VacancyPackagesResponse> => {
      const response = await USERAXIOS.get<VacancyPackagesResponse>(
        "/vacancy_packages",
        {
          params: {
            page: pageParam,
            per_page: pageSize,
            search: searchTerm || undefined,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta) return undefined;
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const packages = useMemo(() => {
    return data?.pages.flatMap((page) => page.data || []) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: packages,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta?.last_page || 0,
    totalItems: data?.pages[0]?.meta?.total || 0,
  };
};
